import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  useMemo,
  useReducer,
  useEffect,
} from 'react';
import { AutoSizer } from 'react-virtualized';

import debounce from 'lodash/debounce';

import Grid from '@gogame/ui/Grid';
import Typography from '@gogame/ui/Typography';
import { useForkRef } from '@gogame/ui/utils';

import ChartContext from './ChartContext';
import { ChartErrorBoundary } from '../Sentry/ChartErrorBoundary';
import { ComposedChart } from './ComposedChart';
import { SCALE_DAILY } from './ComposedChart/constants';
import { Container, LazyChartToolbar } from './Container';
import { PieChart } from './PieChart';
import TriggerProvider from './_Trigger/TriggerProvider';
import {
  chartReducer,
  RESET,
  BRUSH_SET_POSITION,
  BRUSH_RESET,
  HIGHLIGHTING_SET_RANGE,
  ANNOTATION_HOVER_CATEGORY,
  ANNOTATION_SELECT_CATEGORY,
  METRIC_SHOW,
  METRIC_HIDE,
  MOVE_TO_OWN_AXIS,
  MOVE_TO_GROUP_AXIS,
  SET_HIDDEN_METRICS,
  FORCE_RELOAD,
  defaultChartState,
} from './chartReducer';
import {
  useParseChildrenMemo,
  useSortedMetricsMemo,
  useMergedSeriesData,
} from './hooks';
import { initDefaultColorGetter } from './utils';
import withPaper from './withPaper';

let _i = 0;

const defaultMargin = { left: 5, right: 25, top: 5, bottom: 5 };

const Chart = forwardRef(
  (
    {
      children,
      margin = defaultMargin,
      type,
      stacked,
      percentage,
      colorGetter: colorGetterProp,
      legend,
      brush,
      scale = SCALE_DAILY,
      scaleWeekStart,
      sortBy,
      compareMode,
      sidebar,
      actions,
      loading,
      loaderType = 'container', // container | chart
      title,
      affix,
      minWidth,
      hiddenMetrics,
      hiddenToolbar,
      onMetricToggle,
      style,
      pieLabelType,
      lazy,
      onLoadChart,
      settingsPreview,
    },
    ref
  ) => {
    const [id] = useState(() => `chart_${_i++}`);
    const [chartState, dispatch] = useReducer(chartReducer, defaultChartState);
    const controlledToggle = hiddenMetrics !== undefined;
    const hiddenMetricsRef = useRef(undefined);
    const chartRef = useRef(null);
    const readyRef = useRef(false);

    const handleRef = useForkRef(ref, chartRef);

    hiddenMetricsRef.current = hiddenMetrics;

    useEffect(() => {
      if (controlledToggle) {
        dispatch({
          type: SET_HIDDEN_METRICS,
          payload: hiddenMetrics,
        });
      }
    }, [hiddenMetrics]);

    const {
      series,
      metricsTooltip,
      dateAxis,
      metrics,
      unknown,
      noSeriesPlaceholder,
      annotationsMetric,
    } = useParseChildrenMemo({
      children,
      stacked,
      compareMode,
      type,
      percentage,
    });

    const { data, getCategoryPeriod, getBasePeriodDateCategory } =
      useMergedSeriesData(series, scale, scaleWeekStart);

    const defaultColorGetter = useMemo(initDefaultColorGetter, [data]);

    const colorGetter = colorGetterProp || defaultColorGetter;

    const sortedMetrics = useSortedMetricsMemo({ metrics, sortBy });

    const selectedCategoryDate = useMemo(() => {
      if (chartState.annotations.selectedCategory !== null) {
        return getCategoryPeriod(chartState.annotations.selectedCategory)[0];
      }
      return null;
    }, [chartState.annotations.selectedCategory, getCategoryPeriod]);

    const empty = useMemo(() => data.length === 0, [data.length]);
    const noSeries = series.length === 0;

    const hoverAnnotation = useCallback(
      debounce(({ category }) => {
        dispatch({
          type: ANNOTATION_HOVER_CATEGORY,
          payload: category,
        });
      }, 100),
      []
    );

    const selectAnnotations = useCallback((payload) => {
      dispatch({
        type: ANNOTATION_SELECT_CATEGORY,
        payload: payload,
      });
    }, []);

    const handleChangeBrushPosition = useCallback(
      debounce((pos) => {
        dispatch({
          type: BRUSH_SET_POSITION,
          payload: pos,
        });
      }, 600),
      []
    );

    const highlightPeriod = useCallback(
      (period) => {
        if (period) {
          const [from, to] = period;
          dispatch({
            type: HIGHLIGHTING_SET_RANGE,
            payload: {
              start: getBasePeriodDateCategory(from),
              end: getBasePeriodDateCategory(to),
            },
            __applyBrushPosition: ({ endIndex, startIndex }) =>
              chartRef.current.handleBrushChange({
                endIndex: endIndex,
                startIndex: startIndex,
              }),
          });
        } else {
          dispatch({ type: HIGHLIGHTING_SET_RANGE });
        }
      },
      [getBasePeriodDateCategory]
    );

    const handleToggleMetric = useCallback(
      (id, state) => {
        if (!controlledToggle) {
          dispatch({
            type: state ? METRIC_HIDE : METRIC_SHOW,
            payload: id,
          });
        } else {
          onMetricToggle(id, state);
        }
      },
      [controlledToggle, onMetricToggle]
    );

    const handleChangeAxis = useCallback((id, state) => {
      dispatch({
        type: state ? MOVE_TO_OWN_AXIS : MOVE_TO_GROUP_AXIS,
        payload: id,
      });
    }, []);

    useEffect(() => {
      if (readyRef.current) {
        dispatch({
          type: RESET,
          payload: {
            ...defaultChartState,
            hiddenMetrics: controlledToggle
              ? hiddenMetricsRef.current
              : defaultChartState.hiddenMetrics,
            maxIndex: data.length - 1,
          },
        });
      } else {
        readyRef.current = true;
      }
    }, [series]);

    useEffect(
      () => () => {
        if (brush) {
          dispatch({
            type: BRUSH_RESET,
          });
        }
      },
      [brush]
    );

    useEffect(
      () => () => {
        dispatch({
          type: FORCE_RELOAD,
        });
      },
      [legend, metrics]
    );

    return (
      <TriggerProvider>
        <ChartContext.Provider
          value={{
            selectedAnnotations: chartState.annotations.selectedAnnotations,
            selectedCategoryDate,
            highlightPeriod,
            selectAnnotations,
            count: data.length,
          }}
        >
          <Container
            title={title}
            loading={loading}
            loaderType={loaderType}
            actions={actions}
            sidebar={sidebar}
            affix={affix}
            minWidth={minWidth}
            hiddenToolbar={hiddenToolbar || (lazy && noSeries)}
            style={style}
            minContentHeight={lazy && noSeries ? 65 : 220}
            settingsPreview={settingsPreview}
          >
            <ChartErrorBoundary>
              {series.length ? (
                <AutoSizer>
                  {({ height, width }) => {
                    if (type === 'pie') {
                      return (
                        <PieChart
                          height={height}
                          width={width}
                          data={data}
                          metrics={sortedMetrics}
                          colorGetter={colorGetter}
                          compareMode={compareMode}
                          margin={margin}
                          ref={handleRef}
                          chartState={chartState}
                          legend={!empty && legend}
                          onToggleMetric={handleToggleMetric}
                          key={chartState.forceReloadId}
                          labelType={pieLabelType}
                        />
                      );
                    }
                    return (
                      <ComposedChart
                        height={height}
                        width={width}
                        getCategoryPeriod={getCategoryPeriod}
                        showWeekends={scale === SCALE_DAILY}
                        metrics={sortedMetrics}
                        colorGetter={colorGetter}
                        dateAxis={dateAxis}
                        compareMode={compareMode}
                        metricsTooltip={metricsTooltip}
                        annotationsMetric={annotationsMetric}
                        key={chartState.forceReloadId}
                        data={data}
                        margin={margin}
                        ref={handleRef}
                        percentage={percentage}
                        unknown={unknown}
                        legend={!empty && legend}
                        brush={!empty && brush && data.length > 5}
                        chartState={chartState}
                        onAnnotationHover={hoverAnnotation}
                        onAnnotationSelect={selectAnnotations}
                        onChangeBrushPosition={handleChangeBrushPosition}
                        onToggleMetric={handleToggleMetric}
                        onChangeAxis={handleChangeAxis}
                        id={id}
                      />
                    );
                  }}
                </AutoSizer>
              ) : !loading ? (
                <Grid container justifyContent={'center'} alignItems={'center'}>
                  {lazy ? (
                    <LazyChartToolbar onLoadChart={onLoadChart} />
                  ) : (
                    <Typography color={'text.secondary'} align={'center'}>
                      {noSeriesPlaceholder}
                    </Typography>
                  )}
                </Grid>
              ) : null}
            </ChartErrorBoundary>
          </Container>
        </ChartContext.Provider>
      </TriggerProvider>
    );
  }
);

export default withPaper(Chart);
